import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Cookie } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl sm:text-2xl text-ink">{title}</h2>
      <div className="mt-4 space-y-3 text-sm sm:text-base leading-relaxed text-stone-600">{children}</div>
    </section>
  );
}

const NOT_USED = [
  "Cookie di analytics (Google Analytics, Matomo, ecc.)",
  "Pixel pubblicitari (Meta, Google Ads, ecc.)",
  "Cookie di profilazione o marketing",
  "Social widget traccianti o feed incorporati",
  "Strumenti di heatmap o registrazione sessioni",
  "Fingerprinting o tecniche simili",
];

export default function CookiePolicy() {
  useSeo({
    title: "Cookie Policy | Brunch & Bottega",
    description: "Cookie Policy di Brunch & Bottega: questo sito non usa cookie di tracciamento né di profilazione. Scopri esattamente cosa viene (e non viene) installato.",
    path: "/cookie-policy",
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28" data-testid="cookie-page">
      <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Documento legale</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">Cookie Policy</h1>
      <p className="mt-4 text-sm text-stone-500">Ultimo aggiornamento: <span className="bg-clay text-terracotta-dark font-semibold px-1.5 py-0.5 rounded">[DATA DI AGGIORNAMENTO]</span></p>

      <div className="mt-8 bg-forest/5 border border-forest/20 rounded-2xl p-5 flex gap-4" data-testid="cookie-summary">
        <Cookie size={26} className="text-forest shrink-0 mt-1" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-stone-700">
          <strong className="text-ink">In sintesi:</strong> il codice di questo sito <strong>non installa alcun cookie</strong> e
          non utilizza il local storage del browser. L'unica eccezione sono <strong>due cookie tecnici di
          sicurezza impostati automaticamente dalla piattaforma di hosting</strong> (Cloudflare), descritti nella
          tabella qui sotto. <strong>Non mostriamo un banner di consenso</strong> perché allo stato attuale non esiste
          alcuna tecnologia di tracciamento o profilazione che lo richieda: solo cookie tecnici di sicurezza
          e uno script di statistiche di visita senza cookie, entrambi della piattaforma di hosting.
        </p>
      </div>

      <Section title="1. Cosa sono i cookie">
        <p>
          I cookie sono piccoli file di testo che i siti salvano sul tuo dispositivo. Alcuni sono tecnici
          (servono al funzionamento), altri servono ad analisi o pubblicità e richiedono il tuo consenso
          preventivo secondo la normativa italiana ed europea.
        </p>
      </Section>

      <Section title="2. Cookie effettivamente presenti su questo sito">
        <p>
          Il codice di questo sito non crea, legge o utilizza alcun cookie. Verificato con ispezione tecnica
          del browser: l'unica fonte di cookie è la <strong>piattaforma di hosting</strong> che pubblica il sito,
          che si appoggia alla rete Cloudflare. Questi cookie sono impostati automaticamente dall'infrastruttura,
          non sono accessibili al codice del sito (sono HttpOnly) e non vengono usati per marketing o profilazione.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-stone-200 rounded-xl overflow-hidden" data-testid="cookie-table">
            <thead>
              <tr className="bg-sand text-left text-stone-800">
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">Provider</th>
                <th className="px-4 py-3 font-semibold">Scopo</th>
                <th className="px-4 py-3 font-semibold">Durata</th>
                <th className="px-4 py-3 font-semibold">Necessario</th>
                <th className="px-4 py-3 font-semibold">Consenso</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-stone-200 align-top">
                <td className="px-4 py-3 font-mono text-xs">cf_clearance</td>
                <td className="px-4 py-3">Cloudflare, per conto della piattaforma di hosting</td>
                <td className="px-4 py-3">Tecnico: attesta che il browser ha superato la verifica anti-bot/sicurezza</td>
                <td className="px-4 py-3">Circa 1 anno</td>
                <td className="px-4 py-3">Sì (sicurezza)</td>
                <td className="px-4 py-3">No (cookie tecnico)</td>
              </tr>
              <tr className="border-t border-stone-200 align-top">
                <td className="px-4 py-3 font-mono text-xs">__cf_bm</td>
                <td className="px-4 py-3">Cloudflare, per conto della piattaforma di hosting</td>
                <td className="px-4 py-3">Tecnico: distingue visitatori umani da traffico automatizzato (bot management)</td>
                <td className="px-4 py-3">30 minuti</td>
                <td className="px-4 py-3">Sì (sicurezza)</td>
                <td className="px-4 py-3">No (cookie tecnico)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="flex items-start gap-2 text-sm">
          <CheckCircle2 size={18} className="text-forest shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            Entrambi sono cookie tecnici di sicurezza: secondo le linee guida del Garante per la protezione
            dei dati personali, i cookie tecnici non richiedono il consenso preventivo dell'utente, ma solo
            questa informativa. Prima della pubblicazione definitiva è opportuno verificare con il fornitore
            di hosting la configurazione effettiva:
            <span className="bg-clay text-terracotta-dark font-semibold px-1.5 py-0.5 rounded ml-1">[FORNITORE DI HOSTING — da verificare in produzione]</span>.
          </span>
        </p>
      </Section>

      <Section title="3. Cosa NON usiamo">
        <ul className="space-y-2">
          {NOT_USED.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm sm:text-base">
              <XCircle size={18} className="text-terracotta shrink-0 mt-0.5" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="4. Statistiche di visita senza cookie (Cloudflare Web Analytics)">
        <p>
          La piattaforma di hosting carica automaticamente un piccolo script di misurazione
          (<span className="font-mono text-xs">beacon.min.js</span> di Cloudflare Web Analytics) che raccoglie
          statistiche aggregate sulle visite — pagine viste, tempi di caricamento, tipo di browser —
          <strong> senza usare cookie</strong> e senza identificare il singolo visitatore, secondo quanto
          dichiarato dal fornitore. Non è uno strumento scelto o configurato da noi: fa parte
          dell'infrastruttura di hosting e non può essere disattivato dal codice del sito.
        </p>
        <p>
          Lo segnaliamo qui per trasparenza. Poiché non usa cookie e non profila i visitatori, la normativa
          generalmente non richiede il consenso preventivo per questo tipo di misurazione aggregata;
          la valutazione definitiva spetta al titolare con il proprio consulente legale.
        </p>
      </Section>

      <Section title="5. Servizi esterni contattati (senza cookie)">
        <p>
          I caratteri tipografici e tutte le immagini del sito sono <strong>ospitati direttamente su questo
          sito</strong> (self-hosted): nessuna richiesta a Google Fonts, Unsplash o altri CDN di contenuti.
          Le uniche richieste verso terzi sono quelle tecniche della piattaforma di hosting descritte
          sopra (Cloudflare) e lo script infrastrutturale della piattaforma stessa (assets.emergent.sh).
          Dettagli nella <Link to="/privacy-policy" className="underline underline-offset-2 text-forest font-medium">Privacy Policy</Link>.
        </p>
      </Section>

      <Section title="6. E se in futuro cambiasse qualcosa?">
        <p>
          Il sito è progettato per poter integrare in futuro un sistema di gestione del consenso (CMP):
          se verranno attivati analytics, strumenti di marketing o servizi di terze parti che installano cookie,
          verrà mostrato un banner di consenso preventivo e questa pagina verrà aggiornata di conseguenza.
        </p>
      </Section>

      <Section title="7. Come gestire i cookie dal tuo browser">
        <p>
          Anche se non ne usiamo, puoi sempre controllare o eliminare i cookie dalle impostazioni del tuo
          browser (Chrome, Firefox, Safari, Edge). La disattivazione dei cookie non influisce in alcun modo
          sulla navigazione di questo sito.
        </p>
      </Section>
    </article>
  );
}
